import { useEffect, useMemo, useState } from 'react';
import { listClinicPhysiciansAdminClinicPhysiciansGet } from '../../services/api/sdk.gen';
import type { ClinicPhysicianResponse } from '../../services/api/types.gen';
import { formatPhysicianName } from '../../utils/formatting';

interface UsePhysicianOptionsReturn {
  rogerPhysicianOptions: { name: string; id: string }[];
  loadingPhysicians: boolean;
}

/**
 * Fetches clinic physicians for a clinic from the ClinicPhysician model
 * (`/admin/clinic-physicians?clinic_id=...`) and returns them formatted for
 * dropdown use. The returned `id` is a `clinic_physicians.id` UUID — the
 * `episode.clinic_physician_id` / `intake.clinic_physician_id` foreign key.
 */
export function usePhysicianOptions(
  agencyClinicId: string | null | undefined
): UsePhysicianOptionsReturn {
  const [physicians, setPhysicians] = useState<ClinicPhysicianResponse[]>([]);
  const [loadingPhysicians, setLoadingPhysicians] = useState(false);

  useEffect(() => {
    if (!agencyClinicId) {
      setPhysicians([]);
      return;
    }

    let cancelled = false;

    const fetchPhysicians = async () => {
      setLoadingPhysicians(true);
      try {
        const response = await listClinicPhysiciansAdminClinicPhysiciansGet({
          query: { clinic_id: agencyClinicId },
        });
        if (!cancelled && response?.data) {
          setPhysicians(response.data);
        }
      } catch (err) {
        if (!cancelled) {
          console.error('Error fetching physicians:', err);
          setPhysicians([]);
        }
      } finally {
        if (!cancelled) {
          setLoadingPhysicians(false);
        }
      }
    };

    fetchPhysicians();

    return () => {
      cancelled = true;
    };
  }, [agencyClinicId]);

  const rogerPhysicianOptions = useMemo(
    () =>
      physicians
        .map(p => ({ name: formatPhysicianName(p), id: p.id }))
        .sort((a, b) => a.name.localeCompare(b.name)),
    [physicians]
  );

  return { rogerPhysicianOptions, loadingPhysicians };
}
